// =====================================================================
// AVISOS QUE PIENSAN · DESPEGUE F7 (carril integraciones)
// =====================================================================
// Las siete reglas con lo que esta empresa decidió: umbral, modo (cada
// uno / resumen diario), tope por persona al día, y el silencio, que
// lleva motivo y fecha y queda escrito. Nada se apaga «para siempre».
import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { CampoValidado } from '../ui/Formulario';
import ModalConfirmar from '../ui/ModalConfirmar';
import Motivo from '../experiencia/Motivo';

function fecha(iso) {
  try { return new Date(iso).toLocaleString('es-MX', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }); } catch { return iso; }
}

export default function AvisosProactivos() {
  const [reglas, setReglas] = useState(null);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [silenciar, setSilenciar] = useState(null);
  const [corriendo, setCorriendo] = useState(false);

  const cargar = useCallback(() => {
    api.get('/avisos-proactivos/reglas')
      .then((d) => { setReglas(d.reglas); setError(null); })
      .catch((e) => setError(e.message || 'No se pudieron cargar las reglas.'));
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  async function guardar(codigo, cambios) {
    setError(null); setMsg(null);
    try { await api.put(`/avisos-proactivos/reglas/${codigo}`, cambios); setMsg('Guardado.'); cargar(); }
    catch (e) { setError(e.message); }
  }
  async function levantar(codigo) {
    setError(null);
    try { await api.post(`/avisos-proactivos/reglas/${codigo}/silenciar`, { hasta: null }); setMsg('Silencio levantado: la regla vuelve a avisar.'); cargar(); }
    catch (e) { setError(e.message); }
  }
  async function correr() {
    setCorriendo(true); setError(null); setMsg(null);
    try {
      const r = await api.post('/avisos-proactivos/correr', {});
      const emitidos = Object.values(r.resultado).reduce((n, v) => n + (v.emitidos || 0), 0);
      setMsg(emitidos ? `Listo: ${emitidos} aviso(s) nuevo(s) en la campana.` : 'Listo: no hay nada nuevo que avisar hoy.');
    } catch (e) { setError(e.message); }
    finally { setCorriendo(false); }
  }

  if (error && !reglas) return <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5">{error}</p>;
  if (!reglas) return <p className="text-sm text-gray-500">Cargando reglas…</p>;

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="text-sm text-gray-600">
          TEGO revisa estas siete cosas cada mañana y avisa en la campana (y por Telegram a quien lo vinculó): qué pasó, por qué importa y qué hacer.
          Nada se repite el mismo día, hay un tope por persona, y silenciar una regla lleva motivo y fecha.
        </p>
        <button type="button" onClick={correr} disabled={corriendo} className="px-3 py-2 text-sm rounded-lg border border-gray-200 disabled:opacity-50">
          {corriendo ? 'Revisando…' : 'Revisar ahora'}
        </button>
      </div>
      {msg && <p role="status" className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg p-2.5">{msg}</p>}
      {error && <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5">{error}</p>}
      <div className="grid gap-3 md:grid-cols-2">
        {reglas.map((r) => <Regla key={r.codigo} r={r} onGuardar={guardar} onSilenciar={() => setSilenciar(r)} onLevantar={() => levantar(r.codigo)} />)}
      </div>
      {silenciar && <ModalSilenciar r={silenciar} onCerrar={() => setSilenciar(null)} onHecho={() => { setSilenciar(null); setMsg('Regla silenciada; vuelve sola en la fecha.'); cargar(); }} />}
    </div>
  );
}

function Regla({ r, onGuardar, onSilenciar, onLevantar }) {
  const [modo, setModo] = useState(r.modo);
  const [tope, setTope] = useState(r.tope_por_persona_dia);
  const umbral = r.umbral_efectivo || {};
  const [valor, setValor] = useState(Object.values(umbral)[0]);
  const claveUmbral = Object.keys(umbral)[0];
  const cambiado = modo !== r.modo || Number(tope) !== r.tope_por_persona_dia || String(valor) !== String(Object.values(umbral)[0]);
  return (
    <section className={`bg-white border rounded-xl p-4 space-y-2 ${r.silenciada ? 'border-amber-300' : 'border-gray-200'}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 className="font-semibold text-gray-900">{r.nombre}</h2>
          <p className="text-xs text-gray-500">{r.descripcion}</p>
        </div>
        <span className={`text-xs font-semibold px-2 py-0.5 rounded ${r.silenciada ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`}>
          {r.silenciada ? 'Silenciada' : 'Avisa'}
        </span>
      </div>
      {r.silenciada && (
        <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded p-2">
          Hasta el {fecha(r.silenciada_hasta)} · «{r.silencio_motivo}». Vuelve sola en esa fecha.
          <button type="button" onClick={onLevantar} className="ml-2 font-semibold underline">Levantar ahora</button>
        </p>
      )}
      <div className="grid grid-cols-3 gap-2 text-sm">
        {claveUmbral && (
          <CampoValidado etiqueta={`Umbral (${claveUmbral})`} valor={String(valor)} ayuda="A partir de cuánto avisa.">
            {(props) => <input {...props} value={Array.isArray(valor) ? valor.join(',') : valor} onChange={(e) => setValor(e.target.value)} />}
          </CampoValidado>
        )}
        <CampoValidado etiqueta="Cómo avisa" valor={modo}>
          {(props) => (
            <select {...props} value={modo} onChange={(e) => setModo(e.target.value)}>
              <option value="CADA_UNO">Uno por cosa</option>
              <option value="RESUMEN">Resumen diario</option>
            </select>
          )}
        </CampoValidado>
        <CampoValidado etiqueta="Tope por persona y día" valor={String(tope)} ayuda="Más que esto en un día es ruido.">
          {(props) => <input {...props} type="number" min="1" max="100" value={tope} onChange={(e) => setTope(e.target.value)} />}
        </CampoValidado>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <button type="button" disabled={!cambiado} aria-describedby={cambiado ? undefined : `motivo-guardar-${r.codigo}`}
                onClick={() => {
                  const u = { ...umbral };
                  if (claveUmbral) u[claveUmbral] = Array.isArray(umbral[claveUmbral]) ? String(valor).split(',').map((x) => Number(x.trim())).filter((x) => !Number.isNaN(x)) : Number(valor);
                  onGuardar(r.codigo, { umbral: u, modo, tope_por_persona_dia: Number(tope) });
                }}
                className="px-3 py-1.5 text-sm rounded-lg bg-[var(--color-marca)] text-white font-semibold disabled:opacity-50">
          Guardar
        </button>
        {/* BOT-1 (19 sep 2026): por qué está gris, a la vista. */}
        <Motivo id={`motivo-guardar-${r.codigo}`} cuando={!cambiado}>Cambia el umbral, el modo o el tope para poder guardar.</Motivo>
        {!r.silenciada && <button type="button" onClick={onSilenciar} className="px-3 py-1.5 text-sm rounded-lg border border-gray-200">Silenciar un tiempo</button>}
      </div>
    </section>
  );
}

function ModalSilenciar({ r, onCerrar, onHecho }) {
  const [hasta, setHasta] = useState('');
  return (
    <ModalConfirmar
      titulo={`Silenciar «${r.nombre}»`}
      descripcion="Mientras esté silenciada no avisa. En la fecha que pongas vuelve sola, sin que nadie se acuerde. Queda escrito quién y por qué."
      extra={(
        <CampoValidado etiqueta="Hasta cuándo" requerido valor={hasta} validar={(v) => (v ? null : 'Pon una fecha: un silencio sin fecha es apagar la regla.')}>
          {(props) => <input {...props} type="datetime-local" value={hasta} onChange={(e) => setHasta(e.target.value)} />}
        </CampoValidado>
      )}
      pedirMotivo
      etiquetaConfirmar="Silenciar"
      onConfirmar={async (motivo) => {
        await api.post(`/avisos-proactivos/reglas/${r.codigo}/silenciar`, { hasta: new Date(hasta).toISOString(), motivo });
        onHecho();
      }}
      onCerrar={onCerrar}
    />
  );
}
